import type { CSSProperties, ReactNode } from "react"
import { Landmark, Scale, TrendingUp } from "lucide-react"

import type { BookId } from "@/lib/types"
import { cn } from "@/lib/utils"

/**
 * Identidad visual de los tres libros.
 *
 * Un mismo hecho economico vive en statutory, tax y management a la vez. La
 * pantalla tiene que decir en cual estas sin que lo leas, pero sin pintar la
 * interfaz de colores: 2px de lomo, un icono y el color solo en el texto.
 */

export type BookKind = "statutory" | "tax" | "management"

export const BOOK_KIND_OF: Record<BookId, BookKind> = {
  STATUTORY_PE: "statutory",
  TAX: "tax",
  MANAGEMENT: "management",
}

export const BOOK_META: Record<
  BookKind,
  { label: string; short: string; icon: typeof Scale; blurb: string; token: string }
> = {
  statutory: {
    // «Societario» y no «Statutory»: ver `BOOK_LABELS` en `lib/types.ts`.
    label: "Societario",
    short: "SO",
    icon: Scale,
    blurb: "Lo que exige la norma contable local. Es el que ven los socios y el regulador.",
    token: "hsl(var(--book-statutory))",
  },
  tax: {
    label: "Tributario",
    short: "TX",
    icon: Landmark,
    blurb: "Adiciones, deducciones y limites de la determinacion.",
    token: "hsl(var(--book-tax))",
  },
  management: {
    label: "Gerencial",
    short: "MG",
    icon: TrendingUp,
    blurb: "El P&L y el EBITDA con los que se decide.",
    token: "hsl(var(--book-management))",
  },
}

const TEXT: Record<BookKind, string> = {
  statutory: "text-book-statutory",
  tax: "text-book-tax",
  management: "text-book-management",
}

const CHIP: Record<BookKind, string> = {
  statutory: "border-book-statutory/30 bg-book-statutory/10 text-book-statutory",
  tax: "border-book-tax/30 bg-book-tax/10 text-book-tax",
  management: "border-book-management/30 bg-book-management/10 text-book-management",
}

export function BookChip({
  kind,
  className,
  withIcon = true,
  label,
}: {
  kind: BookKind
  className?: string
  withIcon?: boolean
  label?: string
}) {
  const meta = BOOK_META[kind]
  const Icon = meta.icon
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-px text-2xs font-medium",
        CHIP[kind],
        className,
      )}
    >
      {withIcon ? <Icon className="h-3 w-3" /> : null}
      {label ?? meta.label}
    </span>
  )
}

/**
 * Cabecera de columna de libro en el editor de reglas: lomo de color a la
 * izquierda, titulo en el color del libro, explicacion en gris.
 */
export function BookColumn({
  kind,
  children,
  className,
}: {
  kind: BookKind
  children: ReactNode
  className?: string
}) {
  const meta = BOOK_META[kind]
  const Icon = meta.icon
  return (
    <section
      className={cn("spine rounded-md border bg-surface p-3 pl-3.5", className)}
      style={{ "--spine-color": meta.token } as CSSProperties}
    >
      <header className="mb-2 border-b pb-2">
        <div className={cn("flex items-center gap-1.5", TEXT[kind])}>
          <Icon className="h-3.5 w-3.5" />
          <h3 className="text-2xs font-semibold uppercase tracking-[0.09em]">{meta.label}</h3>
        </div>
        <p className="mt-1 text-2xs leading-4 text-muted-foreground">{meta.blurb}</p>
      </header>
      <div className="space-y-2.5">{children}</div>
    </section>
  )
}

export { TEXT as BOOK_TEXT_CLASS }
